import React, { useEffect, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { FaBuilding } from "react-icons/fa";
import U2000Panel from "../../../../u2000Core/components/layout/U2000Panel";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import {
  isOrganizationIconFileAllowed,
  MAX_ORGANIZATION_ICON_SIZE_BYTES,
  ORGANIZATION_ICON_ACCEPT,
  type U3148Organization,
} from "../../../U3148Service";
import { NS } from "../../../U3148";
import { useToast } from "../../../../u2000Core/context/toast/U2000ToastContext";
import {
  getOrganizationIconPending,
  mergeOrganizationIconPending,
  resolveOrganizationIconPreviewUrl,
  revokeOrganizationIconPreview,
} from "./u3148OrganizationIconPending";
import U3148OrganizationIconDeleteDlg from "./U3148OrganizationIconDeleteDlg";

interface U3148OrganizationIconPanelProps<T extends Record<string, unknown>> {
  formStateHandlers: U2000FormStateHandlers<T>;
  t: TFunction;
  canEdit: boolean;
}

function U3148OrganizationIconPanel<T extends U3148Organization & Record<string, unknown>>({ formStateHandlers, t, canEdit }: U3148OrganizationIconPanelProps<T>) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const organization = formStateHandlers.getData() as U3148Organization | null;
  const formState = formStateHandlers.getFormState();
  const isEditing = formState === U2000FormStateEnumState.EDIT;
  const customAttributes = formStateHandlers.getCustomAttributes();
  const pending = getOrganizationIconPending(customAttributes);
  const previewUrl = resolveOrganizationIconPreviewUrl(pending, organization?.organizationIconUrl);
  const hasIcon = Boolean(previewUrl);

  const actionsDisabled = !canEdit || !isEditing;

  useEffect(() => {
    return () => {
      revokeOrganizationIconPreview(getOrganizationIconPending(formStateHandlers.getCustomAttributes()));
    };
  }, [formStateHandlers]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;

    if (file.size > MAX_ORGANIZATION_ICON_SIZE_BYTES) {
      toast.error(t("msgIconFileTooLarge", { ns: NS }));
      return;
    }
    if (!isOrganizationIconFileAllowed(file)) {
      toast.error(t("msgIconFileInvalidType", { ns: NS }));
      return;
    }

    revokeOrganizationIconPreview(pending);
    const previewObjectUrl = URL.createObjectURL(file);
    formStateHandlers.setCustomAttributes(
      mergeOrganizationIconPending(customAttributes, {
        pendingFile: file,
        pendingDelete: false,
        previewObjectUrl,
      })
    );
  };

  const confirmPendingIconDelete = () => {
    revokeOrganizationIconPreview(pending);
    formStateHandlers.setCustomAttributes(
      mergeOrganizationIconPending(customAttributes, {
        pendingFile: null,
        pendingDelete: true,
        previewObjectUrl: null,
      })
    );
  };

  const handleDeleteDialogClose = (result: "yes" | "no") => {
    setIsDeleteDialogOpen(false);
    if (result === "yes") {
      confirmPendingIconDelete();
    }
  };

  const initials =
    organization?.organizationName
      ?.trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?";

  const hasExistingIconActions =
    (hasIcon || Boolean(organization?.organizationIconUrl)) && !pending.pendingDelete;

  return (
    <U2000Panel
      title={t("lblOrganizationIcon", { ns: NS })}
      titleAlign="left"
      className="flex h-full w-full flex-col [&>div.w-full]:flex [&>div.w-full]:min-h-0 [&>div.w-full]:flex-1 [&>div.w-full]:flex-col"
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="mt-3 flex min-h-0 flex-1 w-full items-center justify-center p-2 @container-size">
          {hasIcon ? (
            <img
              src={previewUrl!}
              alt={t("lblOrganizationIcon", { ns: NS })}
              className="h-auto max-h-[100cqh] w-auto max-w-[100cqw] object-contain"
            />
          ) : (
            <div className="flex aspect-square w-[min(100cqw,100cqh)] items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
              <div className="flex flex-col items-center justify-center text-gray-400">
                <FaBuilding className="mb-2 h-16 w-16" aria-hidden />
                <span className="text-2xl font-semibold text-gray-500">{initials}</span>
              </div>
            </div>
          )}
        </div>

        {isEditing && (
          <div className="flex w-full shrink-0 flex-col gap-2">
            <input
              ref={inputRef}
              type="file"
              accept={ORGANIZATION_ICON_ACCEPT}
              className="hidden"
              disabled={actionsDisabled}
              onChange={handleFileChange}
            />
            {pending.pendingDelete && !pending.pendingFile ? (
              <button
                type="button"
                disabled={actionsDisabled}
                onClick={() => {
                  formStateHandlers.setCustomAttributes(
                    mergeOrganizationIconPending(customAttributes, {
                      pendingDelete: false,
                      pendingFile: null,
                      previewObjectUrl: null,
                    })
                  );
                }}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t("btnUndoIconDelete", { ns: NS })}
              </button>
            ) : (
              <div className={hasExistingIconActions ? "flex w-full gap-2" : "flex w-full flex-col gap-2"}>
                <button
                  type="button"
                  disabled={actionsDisabled}
                  onClick={() => inputRef.current?.click()}
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {hasIcon ? t("btnReplaceIcon", { ns: NS }) : t("btnUploadIcon", { ns: NS })}
                </button>
                {hasExistingIconActions && (
                  <button
                    type="button"
                    disabled={actionsDisabled}
                    onClick={() => setIsDeleteDialogOpen(true)}
                    className="flex-1 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {t("btnDeleteIcon", { ns: NS })}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <U3148OrganizationIconDeleteDlg isOpen={isDeleteDialogOpen} onClose={handleDeleteDialogClose} t={t} NS={NS} />
    </U2000Panel>
  );
}

export default U3148OrganizationIconPanel;
